import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { Sparkles } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const STORAGE_KEY = "am_exit_shown_v1";

export default function ExitIntent() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const onLeave = (e) => {
      if (e.clientY <= 0) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setOpen(true);
      }
    };
    const timer = setTimeout(() => document.addEventListener("mouseleave", onLeave), 8000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [pathname]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      toast.error("Please enter your name and email");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ type: "guide_download", ...form, consent: true, notes: "Exit-intent guide download" });
      toast.success("Guide sent! Check your inbox soon.");
      setOpen(false);
      navigate("/guide");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="exit-intent-dialog" className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#10B981]/10 text-[#10B981] mb-2">
            <Sparkles className="h-6 w-6" />
          </div>
          <DialogTitle className="font-heading text-2xl text-[#0B132B]">
            Before you go — get the free NZ Hot Water Upgrade Guide
          </DialogTitle>
          <DialogDescription className="text-[#475569]">
            A simple, no-jargon PDF on choosing the right heat pump hot water system for your Kiwi home.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-2">
          <div>
            <Label className="text-xs">Name</Label>
            <Input data-testid="exit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input data-testid="exit-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </div>
          <div>
            <Label className="text-xs">Phone (optional)</Label>
            <Input data-testid="exit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <Button
            type="submit"
            data-testid="exit-submit-btn"
            disabled={submitting}
            className="w-full rounded-full bg-[#0B132B] hover:bg-[#050914] text-white py-6 font-semibold"
          >
            {submitting ? "Sending..." : "Send Me the Free Guide"}
          </Button>
          <p className="text-[10px] text-[#475569] text-center">
            We respect your inbox. Unsubscribe any time.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
